import { DateRangeFilterValue } from "../../UI/Table/Filters/DateRangeFilter.tsx";
import { AmountRangeFilterValue } from "../../UI/Table/Filters/AmountRangeFilter.tsx";
import { CountRangeFilterValue } from "../../UI/Table/Filters/CountRangeFilter.tsx";
import { formatDateRome } from "../../UI/Utils/formatDateRome.ts";

export type SalesColumnFilter<Row> =
  | { kind: "values"; valuesOf: (row: Row) => string[] }
  | { kind: "dateRange"; utcDateTimeOf: (row: Row) => string | null }
  | { kind: "amountRange"; amountInCentsOf: (row: Row) => number }
  | { kind: "countRange"; countOf: (row: Row) => number };

export type SalesColumnFilterValue =
  | { kind: "values"; selected: string[] }
  | ({ kind: "dateRange" } & DateRangeFilterValue)
  | ({ kind: "amountRange" } & AmountRangeFilterValue)
  | ({ kind: "countRange" } & CountRangeFilterValue);

export type SalesColumnFilters = Record<string, SalesColumnFilterValue>;

export type FilterableColumn<Row> = {
  header: string;
  filter?: SalesColumnFilter<Row>;
};

const CALENDAR_DATE_FORMAT = "YYYY-MM-DD";

export function isActiveColumnFilter(value?: SalesColumnFilterValue): boolean {
  if (!value) return false;
  switch (value.kind) {
    case "values":
      return value.selected.length > 0;
    case "dateRange":
      return !!(value.from || value.to);
    case "amountRange":
      return value.minInCents !== undefined || value.maxInCents !== undefined;
    case "countRange":
      return value.min !== undefined || value.max !== undefined;
  }
}

export function activeColumnFilterCountOf(filters: SalesColumnFilters): number {
  return Object.values(filters).filter(isActiveColumnFilter).length;
}

function isWithinRange<Value extends number | string>(
  value: Value,
  from?: Value,
  to?: Value,
): boolean {
  return (
    (from === undefined || value >= from) && (to === undefined || value <= to)
  );
}

function isDayWithin(
  utcDateTime: string | null,
  { from, to }: DateRangeFilterValue,
): boolean {
  if (!utcDateTime) return false;
  return isWithinRange(
    formatDateRome(utcDateTime, CALENDAR_DATE_FORMAT),
    from,
    to,
  );
}

function matchesColumnFilter<Row>(
  row: Row,
  filter: SalesColumnFilter<Row>,
  value: SalesColumnFilterValue,
): boolean {
  if (filter.kind === "values" && value.kind === "values")
    return filter
      .valuesOf(row)
      .some((rowValue) => value.selected.includes(rowValue));
  if (filter.kind === "dateRange" && value.kind === "dateRange")
    return isDayWithin(filter.utcDateTimeOf(row), value);
  if (filter.kind === "amountRange" && value.kind === "amountRange")
    return isWithinRange(
      filter.amountInCentsOf(row),
      value.minInCents,
      value.maxInCents,
    );
  if (filter.kind === "countRange" && value.kind === "countRange")
    return isWithinRange(filter.countOf(row), value.min, value.max);
  return true;
}

export function rowsMatchingColumnFilters<Row>(
  rows: Row[],
  columns: FilterableColumn<Row>[],
  filters: SalesColumnFilters,
): Row[] {
  const activeFilters = columns.flatMap(({ header, filter }) => {
    const value = filters[header];
    return filter && value && isActiveColumnFilter(value)
      ? [{ filter, value }]
      : [];
  });
  return rows.filter((row) =>
    activeFilters.every(({ filter, value }) =>
      matchesColumnFilter(row, filter, value),
    ),
  );
}

export function valueOptionsOf<Row>(
  rows: Row[],
  valuesOf: (row: Row) => string[],
  selected: string[],
): string[] {
  return [...new Set([...rows.flatMap(valuesOf), ...selected])].sort(
    (first, second) => first.localeCompare(second, "it"),
  );
}
